import type { HolidaySchema } from '@ametista-ai/api-schema';
import type { TFunction } from 'i18next';

/** Unicode regional-indicator pair for a 2-letter ISO country code, or null if not representable. */
export function getCountryFlagEmoji(country: string | null): string | null {
  if (!country) {
    return null;
  }

  const upper = country.toUpperCase();
  if (upper.length !== 2 || !/^[A-Z]{2}$/.test(upper)) {
    return null;
  }

  const codePoints = [...upper].map((c) => 0x1f1e6 + (c.charCodeAt(0) - 0x41));
  return String.fromCodePoint(...codePoints);
}

function regionCodeToDisplayName(regionCode: string, locale: string): string {
  const upper = regionCode.toUpperCase();
  if (upper.length !== 2 || !/^[A-Z]{2}$/.test(upper)) {
    return regionCode;
  }

  try {
    const lang = locale.replaceAll('_', '-');
    const name = new Intl.DisplayNames([lang, 'en'], { type: 'region' }).of(upper);
    if (name) {
      return name;
    }
  } catch {
    // Bad locale tag or unsupported environment
  }

  return upper;
}

export function getHolidayCountryLabel(
  country: string | null,
  locale: string,
  t: TFunction
): string {
  if (!country) {
    return t('holidays.country.custom');
  }

  return regionCodeToDisplayName(country, locale);
}

/** Synced holidays grouped by country name, then chronological; custom (manual) holidays last. */
export function sortHolidaysByCountryThenDate(
  holidays: HolidaySchema[],
  locale: string,
  t: TFunction
): HolidaySchema[] {
  return [...holidays].sort((a, b) => {
    const aCustom = a.country === null;
    const bCustom = b.country === null;
    if (aCustom !== bCustom) {
      return aCustom ? 1 : -1;
    }

    const labelA = getHolidayCountryLabel(a.country, locale, t);
    const labelB = getHolidayCountryLabel(b.country, locale, t);
    const byLabel = labelA.localeCompare(labelB, undefined, { sensitivity: 'base' });
    if (byLabel !== 0) {
      return byLabel;
    }

    return a.date.localeCompare(b.date);
  });
}
